import React from "react";
import { Link } from "react-router-dom";
import Seo, { orgJsonLd } from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { Differentiators } from "@/components/Differentiators";
import { QualificationQuiz } from "@/components/forms/QualificationQuiz";
import { CtaBlock } from "@/components/CtaBlock";
import { Button } from "@/components/ui/button";
import { ArrowRight, CloudRain, Stethoscope, IdCard } from "lucide-react";
import { SpecialOffer } from "@/components/SpecialOffer";
import { ReviewsBar } from "@/components/ReviewsBar";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const websiteJsonLd = {
  "@context": "https://schema.org", "@type": "WebSite", name: "SENIORCAR",
  url: typeof window !== "undefined" ? window.location.origin : "",
  potentialAction: { "@type": "SearchAction", target: `${typeof window !== "undefined" ? window.location.origin : ""}/cerca?q={q}`, "query-input": "required name=q" },
};

export default function Home() {
  const { data: guides = [] } = useQuery({ queryKey: ["guides-home"], queryFn: () => api.get("/guides").then((r) => r.data.slice(0, 3)) });

  return (
    <>
      <Seo title="SENIORCAR — La libertà di continuare a muoversi"
        description="SENIORCAR è un dispositivo medico monoposto per persone con difficoltà motorie: senza patente né targa, autonomia fino a 120 km, protezione dal meteo. Ordina online."
        keywords="dispositivo medico mobilità, mobilità difficoltà motorie, senza patente anziani, seniorcar"
        jsonLd={[orgJsonLd, websiteJsonLd]} />

      <Hero />
      <Differentiators />

      {/* What is SENIORCAR */}
      <section className="bg-white border-y border-slate-200" data-testid="what-is">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_seniorcar-discover/artifacts/744417b78035a848_EADEF8AD-40D3-40B4-AB77-82BCB82C30FA.png"
            alt="La gamma SENIORCAR nei suoi colori: bianco, azzurro e verde acqua" loading="lazy" className="rounded-3xl w-full h-[380px] object-cover object-center border border-slate-200 shadow-lg" />
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Cos'è SENIORCAR</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              SENIORCAR è un dispositivo medico monoposto con abitacolo chiuso, progettato per le persone con patologie permanenti e difficoltà motorie. Senza patente né targa,
              con velocità limitata a 20 km/h e autonomia fino a 120 km.
            </p>
            <ul className="mt-6 space-y-3">
              {[["Protezione dal meteo", CloudRain], ["Progettato come dispositivo medico", Stethoscope], ["Senza patente né targa", IdCard]].map(([t, Ic]) => (
                <li key={t} className="flex items-center gap-3 text-lg text-slate-700"><span className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: "var(--sc-emerald)" }}><Ic size={18} /></span>{t}</li>
              ))}
            </ul>
            <Link to="/seniorcar" className="inline-block mt-7">
              <Button className="h-12 px-6 font-bold text-white rounded-full gap-2" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="home-scopri-seniorcar">Scopri SENIORCAR <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Qualification */}
      <section id="scopri" className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20 scroll-mt-24" data-testid="home-qualification">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="uppercase text-xs font-bold tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">Test rapido</span>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Scopri se SENIORCAR fa per te</h2>
          <p className="mt-3 text-lg text-slate-600">Rispondi a poche domande. Ti aiutiamo a capire se SENIORCAR è la soluzione giusta per te o per un tuo familiare.</p>
        </div>
        <QualificationQuiz />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4 lg:pb-8"><SpecialOffer /></section>

      {/* Guides */}
      {guides.length > 0 && (
        <section className="bg-white border-y border-slate-200" data-testid="home-guides">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Centro Mobilità Senior</h2>
                <p className="mt-3 text-lg text-slate-600">Guide e approfondimenti per scegliere con serenità.</p>
              </div>
              <Link to="/guide"><Button variant="outline" className="font-semibold gap-2 border-2">Tutte le guide <ArrowRight size={16} /></Button></Link>
            </div>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {guides.map((g) => (
                <Link key={g.slug} to={`/guide/${g.slug}`} className="rounded-2xl bg-[var(--sc-canvas)] border border-slate-200 p-6 hover:shadow-md hover:-translate-y-1 transition-all" data-testid={`home-guide-${g.slug}`}>
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">{g.category}</span>
                  <h3 className="mt-2 text-lg font-bold text-slate-900 leading-snug" style={{ fontFamily: "Outfit" }}>{g.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm line-clamp-3">{g.excerpt}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-emerald-800 font-semibold text-sm">Leggi <ArrowRight size={14} /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
        <CtaBlock variant="test" />
      </section>

      <ReviewsBar />
    </>
  );
}
